/**
 * Выбор темы фильтра (#2229, спека 2026-10-11-voice-report-layers.md §2.5,
 * §2.6): что предлагать и как назвать выбранную тему.
 *
 * Читает тот же ответ блока тем, что вкладка (`useVoiceThemesLayer` — один
 * ключ, один запрос; вкладка отдаёт свой слой): набор целиком, фильтром не
 * сужен. На «Маркетинге» и «Продукте» — ответ без срезов: выбору нужны только
 * строки. Выбор есть, когда есть
 * раздел «Темы» (флаг открыт, набор не пуст) и хотя бы одна тема выше порога —
 * или тема уже выбрана: снять её должно быть можно всегда.
 */
import type { VoiceThemeCuts } from '@aica/api-types/schemas';
import { useThemeFilterRefusal } from './voice-theme-filter-context';
import { useVoiceThemesLayer, type VoiceThemesLayer } from './useVoiceThemesLayer';
import type { VoiceSearch } from './voice-search';
import {
  combineThemeFilterVerdicts,
  themeFilterExportUnavailable,
  themeFilterOptions,
  themeFilterVerdict,
  type ThemeFilterAnswer,
  type ThemeFilterOption,
  type ThemeFilterVerdict,
  type VoiceExportTheme,
} from './voice-theme-filter-view-model';

export interface VoiceThemeFilterTheme {
  /** The picker is drawn. */
  shown: boolean;
  options: ThemeFilterOption[];
  /** The selected theme's name — its row's, else the set's, else the code. */
  name: string | undefined;
  /** The selected theme's base (`since`) — the row's; unknown until it is read. */
  since: string | undefined;
  /**
   * The selected theme's revision — its row's definition version and base:
   * what the theme's conversations are counted by (a FAQ draft is filed under it).
   */
  revision: string | undefined;
}

/**
 * The filter's theme over a themes layer the caller already reads — a tab
 * passes its own (`useVoiceThemesLayer`), so the block, the card and the
 * filter share one observer of the answer and of the set.
 */
export function voiceThemeFilterTheme(
  search: VoiceSearch,
  layer: VoiceThemesLayer
): VoiceThemeFilterTheme {
  const selected = search.voiceTheme;
  const themes = layer.ready;
  const row = themes?.rows.find((candidate) => candidate.code === selected);
  const listed = layer.list.data?.themes.find((theme) => theme.code === selected);
  const name =
    selected === undefined ? undefined : (row?.name ?? listed?.name ?? selected);
  const options = themeFilterOptions(themes, selected, name);
  return {
    shown: selected !== undefined || (layer.shown && options.length > 0),
    options,
    name,
    since: row?.since,
    revision: row ? `${row.definitionVersion}:${row.since}` : undefined,
  };
}

/**
 * The filter's theme where no themes layer is read yet: the picker in the
 * section header (`keepPrevious` — it keeps the last window's options while a
 * new window loads instead of leaving the header) and the Marketing and
 * Product and service tabs, which read the rows without cuts.
 */
export function useVoiceThemeFilter(
  search: VoiceSearch,
  onSearchChange: (patch: Partial<VoiceSearch>) => void,
  cut: VoiceThemeCuts | undefined,
  { keepPrevious = false }: { keepPrevious?: boolean } = {}
): VoiceThemeFilterTheme {
  const layer = useVoiceThemesLayer(search, onSearchChange, cut, () => null, {
    keepPrevious,
  });
  return voiceThemeFilterTheme(search, layer);
}

/** What a tab needs of the filter over its narrowed requests. */
export interface VoiceThemeFilterTab {
  verdict: ThemeFilterVerdict;
  /**
   * The filter was refused (403 `themes_disabled`, 400 `theme_filter_invalid`):
   * the section lifts the theme, the tab shows it is loading meanwhile.
   */
  refused: boolean;
  /** The tab's numbers may be drawn: no theme, or every answer echoes it. */
  blocksShown: boolean;
  /**
   * Why the export menu has nothing to export while the numbers are hidden:
   * the narrowed answer is on its way, or it came without the theme's echo.
   * `null` — the numbers are drawn.
   */
  exportUnavailable: 'pending' | 'noNumbers' | null;
  /** Changes against the previous period may be drawn (`prevComparable`). */
  comparable: boolean;
  /** The theme the exports are filed under — only once the filter is applied. */
  exportTheme: VoiceExportTheme | undefined;
  /** The filter strip's props; `null` — no theme selected. */
  strip: {
    verdict: ThemeFilterVerdict;
    name: string;
    since: string | undefined;
  } | null;
}

/**
 * The filter of one tab (#2229): its verdict over the tab's narrowed answers
 * (`answers` — the requests that carry `themeCode`; the themes block is not
 * one), the refusal handed to the section, and what the strip and the exports
 * say.
 */
export function useVoiceThemeFilterTab(
  search: VoiceSearch,
  theme: VoiceThemeFilterTheme,
  answers: readonly (ThemeFilterAnswer & { error: unknown })[]
): VoiceThemeFilterTab {
  const voiceTheme = search.voiceTheme;
  const refusal = useThemeFilterRefusal(
    voiceTheme,
    answers.map((answer) => answer.error)
  );
  const verdict = combineThemeFilterVerdicts(
    answers
      // A request that failed before answering is its block's own error, with
      // its retry — not a filter still on its way (the refusal is above).
      .filter((answer) => answer.data !== undefined || answer.error == null)
      .map((answer) => themeFilterVerdict(voiceTheme, answer))
  );
  const applied = verdict.kind === 'applied' ? verdict.echo : undefined;
  const blocksShown =
    refusal === null && (verdict.kind === 'none' || verdict.kind === 'applied');
  return {
    verdict,
    refused: refusal !== null,
    blocksShown,
    exportUnavailable: themeFilterExportUnavailable(verdict, voiceTheme, blocksShown),
    comparable: applied?.prevComparable ?? true,
    exportTheme:
      applied && theme.name !== undefined
        ? { code: applied.code, name: theme.name, prevComparable: applied.prevComparable }
        : undefined,
    strip:
      voiceTheme === undefined || theme.name === undefined
        ? null
        : {
            // Under a theme `none` means every narrowed request failed: the
            // blocks show their errors and retries, the strip still names the
            // filter and offers «Clear».
            verdict: verdict.kind === 'none' ? { kind: 'unavailable' } : verdict,
            name: theme.name,
            since: theme.since,
          },
  };
}
